<template>
    <van-popup
        class="account-switch-popup"
        position="bottom"
        get-container="#app"
        :value="value"
        :close-on-click-overlay="!busy"
        :overlay-style="{ backgroundColor: 'rgba(0, 0, 0, 0.4)' }"
        @input="$emit('input', $event)"
    >
        <section class="account-switch-panel" role="dialog" aria-modal="true" aria-labelledby="account-switch-title">
            <header class="account-switch-header">
                <h2 id="account-switch-title">切换账号</h2>
                <button type="button" class="add-account" :disabled="busy" @click="$emit('add-account')">添加账号</button>
            </header>

            <div class="account-switch-list" role="radiogroup" aria-label="选择账号">
                <div
                    v-for="accountItem in accounts"
                    :key="keyOf(accountItem)"
                    class="account-switch-item"
                    :class="{ selected: isSelected(accountItem), busy: busy }"
                    role="radio"
                    :aria-checked="isSelected(accountItem) ? 'true' : 'false'"
                    :tabindex="busy ? -1 : 0"
                    @click="selectAccount(accountItem)"
                    @keydown.enter.prevent="selectAccount(accountItem)"
                    @keydown.space.prevent="selectAccount(accountItem)"
                >
                    <svg
                        v-if="isSelected(accountItem)"
                        class="account-radio"
                        width="20"
                        height="20"
                        viewBox="0 0 20 20"
                        fill="none"
                        aria-hidden="true"
                    >
                        <path d="M10 2C5.58172 2 2 5.58172 2 10C2 14.4183 5.58172 18 10 18C14.4183 18 18 14.4183 18 10C18 7.87827 17.1571 5.84344 15.6569 4.34315C14.1566 2.84285 12.1217 2 10 2Z" fill="#EB413D" />
                        <path d="M5.5 10.5L8.5 13.5L14.5 7.5" stroke="white" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" />
                    </svg>
                    <svg
                        v-else
                        class="account-radio"
                        width="20"
                        height="20"
                        viewBox="0 0 20 20"
                        fill="none"
                        aria-hidden="true"
                    >
                        <path d="M10 2.5C11.9891 2.5 13.8972 3.28977 15.3037 4.69629C16.7102 6.10281 17.5 8.01088 17.5 10C17.5 14.1421 14.1421 17.5 10 17.5C5.85786 17.5 2.5 14.1421 2.5 10C2.5 5.85786 5.85786 2.5 10 2.5Z" stroke="#CCCCCC" />
                    </svg>
                    <div class="account-switch-copy">
                        <div class="account-switch-main">
                            <div class="account-name-group">
                                <span class="account-switch-name">{{ accountItem.valorantUserName }}</span>
                                <span v-if="sourceVisible" class="account-source">{{ sourceCaption(accountItem) }}</span>
                            </div>
                            <span class="account-level">{{ levelCaption(accountItem) }}</span>
                        </div>
                        <button
                            type="button"
                            class="delete-account"
                            :disabled="busy"
                            :aria-label="`删除账号 ${accountItem.valorantUserName}`"
                            @click.stop="$emit('delete-account', accountItem)"
                        >
                            <svg class="delete-account-icon" width="13" height="12" viewBox="0 0 13 12" fill="none" aria-hidden="true">
                                <g opacity="0.3">
                                    <path d="M10.5 2.5V10.1323C10.5 10.8876 9.90305 11.5 9.16667 11.5H3.83333C3.09695 11.5 2.5 10.8876 2.5 10.1323V2.5" stroke="white" stroke-linecap="square" />
                                    <rect x="2.49927" width="8" height="1" rx="0.5" fill="white" />
                                    <rect y="2.5" width="13" height="1" rx="0.5" fill="white" />
                                    <rect x="6" y="4.5" width="1" height="5" rx="0.5" fill="white" />
                                </g>
                            </svg>
                        </button>
                    </div>
                </div>
            </div>

            <footer class="account-switch-footer">
                <div class="account-switch-actions">
                    <button type="button" class="cancel-button" :disabled="busy" @click="cancel">取消</button>
                    <button type="button" class="confirm-button" :disabled="busy || !selectedAccount" @click="confirm">确定</button>
                </div>
                <div class="account-switch-safe-area" aria-hidden="true"><i></i></div>
            </footer>
        </section>
    </van-popup>
</template>

<script>
const { accountKey, isAccountActive, normalizeSource } = require("../../assets/js/accountIdentity");

export default {
    name: "ValorantAccountSwitchPopup",
    props: {
        value: { type: Boolean, default: false },
        accounts: { type: Array, default: () => [] },
        activeBindInfoId: { type: [Number, String], default: null },
        activeName: { type: String, default: "" },
        activeSource: { type: String, default: "TRACKER" },
        sourceVisible: { type: Boolean, default: false },
        busy: { type: Boolean, default: false },
    },
    data() {
        return {
            selectedKey: "",
        };
    },
    computed: {
        selectedAccount() {
            return this.accounts.find((accountItem) => this.keyOf(accountItem) === this.selectedKey) || null;
        },
    },
    watch: {
        value: {
            immediate: true,
            handler(open) {
                if (open) this.resetSelection();
            },
        },
        accounts: {
            deep: true,
            handler() {
                if (this.value && !this.selectedAccount) this.resetSelection();
            },
        },
    },
    methods: {
        keyOf(accountItem) {
            return accountKey(accountItem);
        },
        resetSelection() {
            const activeAccount = this.accounts.find((accountItem) => isAccountActive(
                accountItem,
                this.activeBindInfoId,
                this.activeName,
                this.activeSource
            ));
            this.selectedKey = activeAccount ? this.keyOf(activeAccount) : this.accounts[0] ? this.keyOf(this.accounts[0]) : "";
        },
        isSelected(accountItem) {
            return this.keyOf(accountItem) === this.selectedKey;
        },
        selectAccount(accountItem) {
            if (!this.busy) this.selectedKey = this.keyOf(accountItem);
        },
        sourceCaption(accountItem) {
            return normalizeSource(accountItem && accountItem.dataSource) === "RIOT_PD" ? "官方" : "Tracker";
        },
        levelCaption(accountItem) {
            const level = accountItem && accountItem.level;
            return `LV ${level === null || level === undefined || level === "" ? "--" : level}`;
        },
        cancel() {
            if (!this.busy) {
                this.$emit("input", false);
                this.$emit("cancel");
            }
        },
        confirm() {
            if (!this.busy && this.selectedAccount) this.$emit("confirm", this.selectedAccount);
        },
    },
};
</script>

<style lang="scss" scoped>
.account-switch-popup {
    width: 100%;
    max-width: none;
    max-height: 455px;
    left: 0;
    transform: none;
    overflow: hidden;
    border-radius: 12px 12px 0 0;
    background: #222226;
    font-family: "PingFang SC", -apple-system, BlinkMacSystemFont, "Microsoft YaHei", sans-serif;
    letter-spacing: 0;
}

.account-switch-panel {
    display: flex;
    flex-direction: column;
    width: 100%;
    max-height: 455px;
    padding-top: 16px;
    box-sizing: border-box;
    color: #d7d7d9;
}

.account-switch-header {
    display: flex;
    flex: 0 0 22px;
    align-items: center;
    justify-content: space-between;
    width: calc(100% - 32px);
    height: 22px;
    margin: 0 16px 16px;

    h2 {
        min-width: 0;
        margin: 0;
        color: #d7d7d9;
        font-size: 16px;
        font-weight: 500;
        line-height: 22px;
    }
}

.add-account {
    height: 22px;
    padding: 0;
    border: 0;
    background: transparent;
    color: #a3a3a6;
    font: inherit;
    font-size: 11px;
    line-height: 14px;

    &:disabled { opacity: 0.5; }
}

.account-switch-list {
    display: flex;
    flex: 1 1 auto;
    flex-direction: column;
    gap: 5px;
    min-height: 48px;
    margin: 0 16px 16px;
    overflow-x: hidden;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
}

.account-switch-item {
    display: flex;
    flex: 0 0 48px;
    align-items: center;
    width: 100%;
    height: 48px;
    padding: 12px;
    box-sizing: border-box;
    overflow: hidden;
    border-radius: 2px;
    background: #2e2e33;
    outline: none;

    &.busy { cursor: default; }
    &:focus-visible { box-shadow: inset 0 0 0 1px #eb413d; }
}

.account-radio {
    display: block;
    flex: 0 0 20px;
    width: 20px;
    height: 20px;
    margin-right: 12px;
}

.account-switch-copy {
    display: flex;
    flex: 1 1 auto;
    align-items: center;
    gap: 4px;
    min-width: 0;
    height: 24px;
}

.account-switch-main {
    display: flex;
    flex: 1 1 auto;
    align-items: center;
    justify-content: space-between;
    min-width: 0;
}

.account-name-group {
    display: flex;
    flex: 1 1 auto;
    align-items: center;
    gap: 4px;
    min-width: 0;
}

.account-switch-name {
    min-width: 0;
    overflow: hidden;
    color: #d7d7d9;
    font-size: 13px;
    font-weight: 500;
    line-height: 16px;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.account-source {
    display: flex;
    flex: 0 0 auto;
    align-items: center;
    height: 21px;
    padding: 0 4px;
    box-sizing: border-box;
    border-radius: 4px;
    background: rgba(255, 255, 253, 0.1);
    color: rgba(255, 255, 255, 0.6);
    font-size: 11px;
    font-weight: 400;
    line-height: 11px;
}

.account-level {
    flex: 0 0 auto;
    margin-left: 8px;
    color: #a3a3a6;
    font-size: 11px;
    font-weight: 400;
    line-height: 14px;
    white-space: nowrap;
}

.delete-account {
    display: flex;
    flex: 0 0 24px;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    padding: 0;
    border: 0;
    background: transparent;

    .delete-account-icon { display: block; width: 13px; height: 12px; }
    &:disabled { opacity: 0.5; }
}

.account-switch-footer {
    flex: 0 0 83px;
    height: 83px;
    box-sizing: border-box;
    overflow: hidden;
    border-top: 0.5px solid #323238;
    background: #222226;
}

.account-switch-actions {
    display: flex;
    align-items: center;
    gap: 10px;
    height: 45px;
    padding: 5px 16px 4px;
    box-sizing: border-box;
}

.cancel-button,
.confirm-button {
    display: flex;
    flex: 1 1 0;
    align-items: center;
    justify-content: center;
    height: 40px;
    padding: 8px;
    box-sizing: border-box;
    border: 0;
    border-radius: 4px;
    font: inherit;
    font-size: 16px;
    font-weight: 400;
    line-height: 24px;
}

.cancel-button {
    background: transparent;
    color: #999;
}

.confirm-button {
    background: #fffffd;
    color: #212121;

    &:disabled { opacity: 0.5; }
}

.account-switch-safe-area {
    display: flex;
    align-items: flex-end;
    justify-content: center;
    width: 100%;
    height: 38px;
    padding: 20px 0 9px;
    box-sizing: border-box;

    i {
        display: block;
        width: 134px;
        height: 5px;
        border-radius: 100px;
        background: #fffffd;
    }
}
</style>
